import { queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import { ProgramResponsesFetchError } from './program-responses-fetch-error';
import type { ProgramExportType } from '@eureca/xlsx-export';

/**
 * EU-654 — tipo de base ofertável no dropdown "Tipo de base:". `complete` é a
 * base Completa (sempre disponível); os demais reusam as chaves de wire do
 * export do EU-561.
 */
export type ProgramBaseType =
  | 'complete'
  /**
   * DEV-498 — "Currículos em massa" / "Laudos em massa": sempre ofertadas pelo
   * backend (AC-03); não são bases de respostas — a exportação é assíncrona e
   * termina por e-mail (hook `useBulkDocumentExport`).
   */
  | 'bulk-resumes'
  | 'bulk-attestations'
  | Exclude<ProgramExportType, 'form'>;

/** Tipos cujo clique é a exportação em massa (DEV-498), não o download de base. */
export type BulkDocumentType = 'resumes' | 'attestations';

export function bulkDocumentTypeOf(type: ProgramBaseType): BulkDocumentType | null {
  if (type === 'bulk-resumes') return 'resumes';
  if (type === 'bulk-attestations') return 'attestations';
  return null;
}

/** Labels do dropdown — RN-01/02 (renomeação inclusa) + DEV-498. */
export const PROGRAM_BASE_LABEL: Record<ProgramBaseType, string> = {
  complete: 'Completa',
  'bulk-resumes': 'Currículos em massa',
  'bulk-attestations': 'Laudos em massa',
  'fit-cultural': 'FIT Cultural',
  'learning-map': 'Raio-X Comportamental',
  'career-xray': 'Raio-X de Carreira',
};

async function fetchProgramExportAvailability(
  programId: string,
  signal?: AbortSignal
): Promise<ProgramBaseType[]> {
  const res = await fetchClient<{ types: ProgramBaseType[] }>({
    method: 'GET',
    url: `/programs/${programId}/export-availability`,
    signal,
  });
  if (res.error) {
    throw new ProgramResponsesFetchError(
      `Failed to load program export availability (status ${res.error.status})`,
      res.error.status
    );
  }
  return (res.data as { types: ProgramBaseType[] }).types;
}

/**
 * Disponibilidade dos tipos de base do programa (RN-02/03) — o BACKEND decide;
 * o SPA só renderiza a lista na ordem recebida. Buscada de forma LAZY (quando
 * o menu/dropdown abre) via `enabled` no call-site.
 */
export function programExportAvailabilityQueryOptions(programId: string) {
  return queryOptions({
    queryKey: ['recruiter', 'programs', programId, 'export-availability'] as const,
    queryFn: ({ signal }) => fetchProgramExportAvailability(programId, signal),
    staleTime: 30_000,
  });
}
